/* src/pages/ContractorITServices.js */

import React from "react";
import PageSection from "../components/PageSection";
import { Helmet } from "react-helmet-async";
import styles from "./Pages.module.css";
import { Link } from "react-router-dom";

function ContractorITServices() {
  const faqs = [
    {
      question: "I only have one or two computers. Is it worth hiring IT support?",
      answer:
        "Yes, especially for a one- or two-person operation. When your one computer breaks down, you can't send quotes, schedule jobs, or process invoices. Having a local IT person you can call means you're back up fast, not waiting days for a big box store appointment.",
    },
    {
      question: "Do you work with QuickBooks, Jobber, or Housecall Pro?",
      answer:
        "I handle the IT side, hardware performance, network connectivity, and making sure your systems are set up correctly to run your software. For software-specific issues, I coordinate with the vendor's support so you're not stuck in the middle. Most performance problems are IT problems, not software problems.",
    },
    {
      question: "What if I need help on short notice?",
      answer:
        "Members get priority scheduling for remote sessions. Most remote issues can be resolved in under an hour once we connect. Non-members are seen as schedule allows. If you need regular, dependable access, a monthly plan is the right fit.",
    },
    {
      question: "Can you come to my shop or do you only work remotely?",
      answer:
        "Both. Most issues can be resolved remotely, screen sharing, remote access, and a quick call. When something needs hands-on attention, I come to you. I serve Shreveport and Bossier City with both options.",
    },
  ];

  return (
    <PageSection title="IT Support for Contractors & Trades in Shreveport-Bossier" cta faqs={faqs}>
      <Helmet>
        <title>IT Support for Contractors in Shreveport-Bossier | Tharpe Industries</title>
        <meta
          name="description"
          content="IT support for HVAC, plumbing, electrical, and general contractors in Shreveport and Bossier City. Keep your office running, your invoicing working, and your customer data protected."
        />
        <meta
          name="keywords"
          content="IT support contractors Shreveport, HVAC company IT support, trades business IT, contractor computer repair Bossier City, small business IT support Shreveport"
        />
        <link rel="canonical" href="https://tharpeindustries.com/it-support-contractors" />
      </Helmet>

      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <h2>Keep your invoicing, scheduling, and office systems running, so you can stay focused on the job.</h2>
          <div className={styles.heroQuickCta}>
            <Link to="/it-health-check" className={styles.heroQuickCtaBtn}>Book an IT Health Check, $149 →</Link>
          </div>
          <p>
            When you're running an HVAC, plumbing, electrical, or general contracting business, your office tech needs to work as hard as you do. Quotes go out, invoices come in, job scheduling depends on reliable software, and when the computer freezes or the Wi-Fi dies, you lose time and money.
          </p>
          <p>
            Austin Tharpe helps Shreveport-Bossier contractors keep their office systems running smoothly. Whether it's one computer at home base or a small office with a few desktops and a networked printer, I handle the IT so you can handle the work.
          </p>

          <h3>Why contractors have unique IT challenges</h3>
          <ul>
            <li><strong>Your business software matters more than you think:</strong> ServiceTitan, Jobber, Housecall Pro, QuickBooks, your scheduling and invoicing software is the backbone of your business. Slow or unreliable computers mean missed jobs and late invoices.</li>
            <li><strong>Customer data needs to be protected:</strong> Names, addresses, payment info, job history, contractors hold more customer data than they realize. A compromised computer or stolen laptop is a real liability.</li>
            <li><strong>No IT department to call:</strong> When something breaks, you don't have a tech on staff. You Google it, waste an hour, and still aren't sure if it's fixed. That's exactly what I'm here for.</li>
            <li><strong>Remote and mobile work creates risk:</strong> Tablets and laptops on job sites, Wi-Fi at the shop, cloud access from the truck, each one is a potential weak point if it's not set up correctly.</li>
          </ul>

          <h3>What Tharpe Industries handles for contractors</h3>
          <ul>
            <li><strong>Computer and workstation support:</strong> Slow desktops, crashed laptops, printers that won't cooperate, I handle diagnostics, repairs, and replacements. No waiting on hold with a manufacturer.</li>
            <li><strong>Business software setup and troubleshooting:</strong> QuickBooks running slow? Jobber not syncing? I handle the IT side, hardware performance, network connectivity, and software installation. For software-specific bugs, I work with the vendor's support so you don't have to.</li>
            <li><strong>Backup for your customer and business data:</strong> Your customer list, job history, and financial records need to be backed up automatically. If a hard drive fails, you shouldn't be starting from zero. I set up reliable backup and verify it actually works.</li>
            <li><strong>Network and Wi-Fi at the shop:</strong> Spotty Wi-Fi in the office? Slow internet affecting your scheduling software? I handle network diagnostics and setup so your shop stays connected.</li>
            <li><strong>Basic security for your business:</strong> Antivirus, software updates, secure passwords, the basics that most small businesses skip until something goes wrong. I make sure your systems have reasonable protection without overcomplicating it.</li>
          </ul>

          <div className={styles.ctaContainer}>
            <p><strong>Ready to see where your contracting business's IT actually stands?</strong></p>
            <p className={styles.emergencyText}>Start with the $149 IT Health Check. I'll review your current setup, software performance, backup security, and network reliability, then give you a clear picture of what's working and what needs attention.</p>
            <Link to="/it-health-check" className={styles.ctaButton}>Start with the IT Health Check</Link>
          </div>

          <div className={styles.secondaryCta}>
            <p>Have questions about your shop's specific setup? <Link to="/contact-us">Send me a message</Link> and I'll help you think through what you need.</p>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '0.75rem' }}>I also serve other local businesses:</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem' }}>
              <li><Link to="/it-support-medical-offices" style={{ fontSize: '0.9rem' }}>Medical Offices</Link></li>
              <li><Link to="/it-support-dental-offices" style={{ fontSize: '0.9rem' }}>Dental Practices</Link></li>
              <li><Link to="/it-support-law-firms" style={{ fontSize: '0.9rem' }}>Law Firms</Link></li>
              <li><Link to="/it-support-accounting-firms" style={{ fontSize: '0.9rem' }}>Accounting Firms</Link></li>
              <li><Link to="/it-support-restaurants" style={{ fontSize: '0.9rem' }}>Restaurants</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </PageSection>
  );
}

export default ContractorITServices;
